import { humanize } from 'src/util/text';

export enum AircraftType {
  Unknown = 'Unknown',
  RotorWing = 'RotorWing',
  FixedWing = 'FixedWing',
  Ground = 'Ground',
}

export enum WaypointRole {
  Unknown = 'Unknown',
  PatientPickup = 'PatientPickup',
  PatientDropoff = 'PatientDropoff',
  Other = 'Other',
  Post = 'Post',
}
export enum CrewRole {
  Unknown = 'Unknown',
  PilotInCommand = 'PilotInCommand',
  CoPilot = 'CoPilot',
  Clinical = 'Clinical',
  Other = 'Other',
}

export type PilotRole = CrewRole.PilotInCommand | CrewRole.CoPilot;

export function isPilot(role: CrewRole): role is PilotRole {
  return role === 'PilotInCommand' || role === 'CoPilot';
}
export enum TimestampType {
  DepartBase = 'DepartBase',
  ArrivePickup = 'ArrivePickup',
  DepartPickup = 'DepartPickup',
  ArriveDropoff = 'ArriveDropoff',
  DepartDropoff = 'DepartDropoff',
  ArriveBase = 'ArriveBase',
  Other = 'Other',
}
export enum FlightStatus {
  Unknown = 'Unknown',
  AwaitingLaunch = 'AwaitingLaunch',
  EnRoute = 'EnRoute',
  AtWaypoint = 'AtWaypoint',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
}
export enum FlightType {
  Reposition = 'Reposition',
  Maintenance = 'Maintenance',
  Training = 'Training',
  PatientTransportInterfacility = 'PatientTransportInterfacility',
  PatientTransportScene = 'PatientTransportScene',
  NonTransport = 'NonTransport',
  DemoPR = 'DemoPR',
  SeekAndAssist = 'SeekAndAssist',
  Refuel = 'Refuel',
  CompanyBusiness = 'CompanyBusiness',
  Simulator = 'Simulator',
  ExternalCommercial = 'ExternalCommercial',
  Part133 = 'Part133',
}

export enum UserRole {
  Developer = 'Developer',
  CommunicationsSupervisor = 'CommunicationsSupervisor',
  AviationSupervisor = 'AviationSupervisor',
  Communications = 'Communications',
  Pilot = 'Pilot',
  ProgramManager = 'ProgramManager',
  Biller = 'Biller',
  Executive = 'Executive',
  GlobalDataManager = 'GlobalDataManager',
  GlobalPermissionsManager = 'GlobalPermissionsManager',
  OperationsDataManager = 'OperationsDataManager',
  AviationDataManager = 'AviationDataManager',
  AviationPermissionsManager = 'AviationPermissionsManager',
  OperationsPermissionsManager = 'OperationsPermissionsManager',
  ComplianceManager = 'ComplianceManager',
  RemoteUser = 'RemoteUser',
  PrdUser = 'PrdUser',
  PrdAdmin = 'PrdAdmin',
  FlightLogViewer = 'FlightLogViewer',
}

export type NonExternalFlightType =
  | FlightType.Reposition
  | FlightType.Maintenance
  | FlightType.Training
  | FlightType.PatientTransportInterfacility
  | FlightType.PatientTransportScene
  | FlightType.NonTransport
  | FlightType.DemoPR
  | FlightType.SeekAndAssist
  | FlightType.Refuel
  | FlightType.CompanyBusiness
  | FlightType.Part133;

export const FlightTypesNonExternal: NonExternalFlightType[] = [
  FlightType.Reposition,
  FlightType.Maintenance,
  FlightType.Training,
  FlightType.PatientTransportInterfacility,
  FlightType.PatientTransportScene,
  FlightType.NonTransport,
  FlightType.DemoPR,
  FlightType.SeekAndAssist,
  FlightType.Refuel,
  FlightType.CompanyBusiness,
  FlightType.Part133,
];

export type ExternalFlightType = FlightType.Simulator | FlightType.ExternalCommercial;

export const FlightTypesExternal: ExternalFlightType[] = [
  FlightType.Simulator,
  FlightType.ExternalCommercial,
];

export function isExternalFlightType(
  flightType: FlightType | null | undefined,
): flightType is ExternalFlightType {
  return flightType != null ? FlightTypesExternal.includes(flightType as any) : false;
}

export interface AccessibleAirCarrierDto {
  id: string;
}
export interface CompanyDto {
  id: string;
  name: string;
}

export interface LocationDto {
  id: string;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
  coordinate: LatLongDto;
}

export interface LatLongDto {
  latitude: number | null;
  longitude: number | null;
}

export interface CrewMemberDto {
  id: string;
  employeeId: string;
  role: CrewRole;
  firstName: string;
  lastName: string;
}

export enum CommandResultStatus {
  Succeeded = 'Succeeded',
  Failed = 'Failed',
}

export interface CommandError {
  code: number;
  message: string;
}
export interface CommandResult {
  errors: CommandError[];
  status: CommandResultStatus;
}

export function flightStatusDisplay(status: FlightStatus): string {
  return humanize(status);
}

export function aircraftTypeDisplay(type: AircraftType): string {
  return humanize(type);
}

export function flightTypeDisplay(type: FlightType): string {
  if (type != null) {
    if (type === FlightType.DemoPR) {
      return 'Demo/PR';
    }
    if (type === FlightType.SeekAndAssist) {
      return 'Seek and Assist';
    }
    if (type === FlightType.Reposition) {
      return 'Reposition/Ferry';
    }
    if (type === FlightType.Part133) {
      return 'Part 133';
    }
  }

  return humanize(type);
}

export function crewRoleDisplay(role: CrewRole): string {
  return role === CrewRole.PilotInCommand
    ? 'PIC'
    : role === CrewRole.CoPilot
    ? 'Co-Pilot'
    : humanize(role);
}
